/**
 * Auth Error Sanitizer
 * Maps raw Supabase auth error messages to user-friendly messages.
 * Prevents leaking internal details (project IDs, rate limit specifics, etc.)
 */

const ERROR_MAP = [
  {
    pattern: /invalid login credentials/i,
    message: 'Incorrect email or password. Please check your credentials and try again.'
  },
  {
    pattern: /email rate limit exceeded/i,
    message: 'Too many attempts. Please wait a few minutes before trying again.'
  },
  {
    pattern: /rate limit/i,
    message: 'Too many requests. Please wait a moment and try again.'
  },
  {
    pattern: /captcha protection/i,
    message: 'Security verification failed. Please complete the CAPTCHA and try again.'
  },
  {
    pattern: /email not confirmed/i,
    message: 'Please verify your email address before signing in. Check your inbox for the confirmation link.'
  },
  {
    pattern: /user already registered/i,
    message: 'An account with this email already exists. Try signing in instead.'
  },
  {
    pattern: /signup is disabled/i,
    message: 'New account registration is currently unavailable. Please contact support.'
  },
  {
    pattern: /password.*too short|password.*length/i,
    message: 'Password must be at least 8 characters long.'
  },
  {
    pattern: /password.*weak|password.*leaked|password.*common/i,
    message: 'This password is too common. Please choose a stronger password.'
  },
  {
    pattern: /new password should be different/i,
    message: 'Your new password must be different from your current password.'
  },
  {
    pattern: /auth session missing/i,
    message: 'Your session has expired. Please sign in again.'
  },
  {
    pattern: /refresh_token/i,
    message: 'Your session has expired. Please sign in again.'
  },
  {
    pattern: /network|fetch|timeout|ECONNREFUSED/i,
    message: 'Connection error. Please check your internet and try again.'
  },
  {
    pattern: /too many requests/i,
    message: 'Too many requests. Please wait a moment and try again.'
  },
  {
    pattern: /unable to validate email/i,
    message: 'Please enter a valid email address.'
  },
  {
    pattern: /otp.*expired|token.*expired|link.*expired/i,
    message: 'This link has expired. Please request a new one.'
  }
];

/**
 * Sanitize a Supabase auth error message into a user-friendly message.
 * @param {string} rawMessage - The raw error message from Supabase
 * @returns {string} A user-friendly error message
 */
export function sanitizeAuthError(rawMessage) {
  if (!rawMessage || typeof rawMessage !== 'string') {
    return 'Something went wrong. Please try again later.';
  }

  for (const { pattern, message } of ERROR_MAP) {
    if (pattern.test(rawMessage)) {
      return message;
    }
  }

  // Fallback: never show the raw message
  return 'Something went wrong. Please try again later.';
}

export default sanitizeAuthError;
